import React from 'react';
import { Typography, Box, Chip, Button, Grid } from '@mui/material';
import { fetchPatient } from '../../../../db';
import { EnhancedTable } from '../../../../components/general/EnhancedTable';
import { DocumentPDF } from '../../Document';
import { IconGenerator } from '../../../../components/general/mini_components';
import { Translate } from 'react-localize-redux';
import Modal from '../../../../components/general/modal';

interface BillingLine {
    totalPaidInPeriod: number;
    billItemId: number;
    billReference: number | string;
    billUuid: string;
    category: string | null;
    concept: string;
    department: string;
    departmentUuid: string;
    latestPaymentDate: string | null;
    patient: string;
    patientInvestigationUuid: string | null;
    practitioner: string | null;
    service: string | null;
}

interface PatientCategoryBillingProps {
  data: BillingLine[];
  startDate?: number | null;
  endDate?: number | null;
}

const formatDate = (dateString: string | null) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

const formatAmount = (amount: number | undefined | null) => {
  if (amount == null || isNaN(amount)) return '0.00 FCFA';
  return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' FCFA';
};

const headCells = [
    { id: 'latestPaymentDate', alignment: 'left', label: <Translate id="hospital.analytics.finance.patient_category_billing.payment_date" /> },
    { id: 'totalPaidInPeriod', alignment: 'right', label: <Translate id="hospital.analytics.finance.patient_category_billing.amount_paid" /> },
    { id: 'patient', alignment: 'left', label: <Translate id="hospital.analytics.finance.patient_category_billing.patient" /> },
    { id: 'practitioner', alignment: 'left', label: <Translate id="hospital.analytics.finance.patient_category_billing.practitioner" /> },
    { id: 'category', alignment: 'left', label: <Translate id="hospital.analytics.finance.patient_category_billing.category" /> },
    { id: 'department', alignment: 'left', label: <Translate id="hospital.analytics.finance.patient_category_billing.department" /> },
    { id: 'paymentMethods', alignment: 'left', label: <Translate id="hospital.analytics.finance.patient_category_billing.payment_methods" /> },
];

const PatientCategoryBilling: React.FC<PatientCategoryBillingProps> = ({ data, startDate, endDate }) => {
  const [patientNames, setPatientNames] = React.useState<{ [uuid: string]: string }>({});
  const [showPrintModal, setShowPrintModal] = React.useState(false);

  // Format dates for display
  const formatDateRange = () => {
    if (!startDate || !endDate) return '';
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    };
    
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  // SHER-853, defect 3. This used to await each patient in turn and call setPatientNames
  // once per patient. On the reported CHOM window that is 297 distinct patients over 1234
  // lines, so it was 297 separate renders of the whole table -- measured at 62.6 s of
  // total blocking time, with the page unresponsive to a trivial evaluate for 77.9 s.
  //
  // One await for the whole set and one state write at the end: the lookups still run
  // concurrently, but the table re-renders once. Settled rather than all-or-nothing,
  // because a single unreadable patient used to be harmless when every lookup had its own
  // state write, and must stay harmless now that they share one.
  React.useEffect(() => {
    let cancelled = false;
    const pending = Array.from(new Set(data.map(row => row.patientInvestigationUuid)))
      .filter((uuid): uuid is string => typeof uuid === 'string' && uuid.length > 0)
      .filter((uuid) => !patientNames[uuid]);

    if (pending.length === 0) {
      return undefined;
    }

    (async () => {
      const resolved = await Promise.all(pending.map(async (uuid) => {
        try {
          const patient = await fetchPatient(uuid);
          return [uuid, patient ? `${patient.personalData.name} ${patient.personalData.surnames}` : uuid] as const;
        } catch (error) {
          return [uuid, uuid] as const;
        }
      }));
      if (cancelled) {
        return;
      }
      setPatientNames(prev => ({ ...prev, ...Object.fromEntries(resolved) }));
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">No billing lines available</Typography>
        <Typography color="textSecondary">
          No data was returned by the API or the format is not supported.
        </Typography>
      </Box>
    );
  }

  // Prepare rows for EnhancedTable
  const rows = data.map((row) => ({
    id: row.billItemId || Math.random(), // EnhancedTable expects a unique id
    concept: row.concept || '',
    totalPaidInPeriod: (
      <Typography variant="body2" sx={{ fontWeight: 'bold'}}>
        {formatAmount(row.totalPaidInPeriod)}
      </Typography>
    ),
    department: row.department,
    category: (
      <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
        {Object.entries((row as any).categoryAllocations || {})
          .filter(([key, value]) => typeof value === 'number' && value > 0)
          .map(([key, value], index, array) => (
            <React.Fragment key={key}>
              <span style={{ fontWeight: 'bold' }}>{key}</span>
              <span>: {formatAmount(value as number)}</span>
              {index < array.length - 1 && ', '}
            </React.Fragment>
          ))}
      </Typography>
    ),
    patient: (
      <Typography variant="body2" sx={{ fontWeight: '500' }}>
        {row.patientInvestigationUuid ? (patientNames[row.patientInvestigationUuid] || row.patient) : row.patient}
      </Typography>
    ),
    latestPaymentDate: (
      <Typography variant="body2" sx={{ color: '#666' }}>
        {formatDate(row.latestPaymentDate)}
      </Typography>
    ),
    paidDateRaw: row.latestPaymentDate,
    practitioner: row.practitioner,
    paymentMethods: (
      <Typography variant="body2" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
        {Object.entries((row as any).paymentMethods || {})
          .filter(([key, value]) => typeof value.amount === 'number' && value.amount > 0)
          .map(([key, value], index, array) => (
            <React.Fragment key={key}>
              <span style={{ fontWeight: 'bold' }}>{key}</span>
              <span>: {formatAmount(value.amount as number)}</span>
              {
                key === 'Cheque' && (
                    <span>: Bank: {value.bankName} - Cheque: {value.chequeNumber}</span>
                )
              }
              {index < array.length - 1 && ', '}
            </React.Fragment>
          ))}
      </Typography>
    ),
  }));

  const renderPrintModal = () => {
    if (!showPrintModal) return null;
    
    return (
      <Modal key="modal" fullWidth medium open={showPrintModal} title="" closeModal={() => setShowPrintModal(false)}>
        <div>
          <style>
            {`
              #print {
                width: 100% !important;
                max-width: 1000px !important;
              }
              @media print {
                #print {
                  width: 100% !important;
                  max-width: none !important;
                }
              }
            `}
          </style>
          <DocumentPDF size="A4" name="Patient Category Billing Report">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h4" sx={{ textAlign: 'center', mb: 3, fontWeight: 'bold' }}>
                  <Translate id="hospital.analytics.finance.patient_category_billing.report_title" />
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <div style={{ width: '100%', overflowX: 'auto' }}>
                  <EnhancedTable
                    noHeader
                    noSelectable
                    noFooter
                    rows={rows}
                    headCells={headCells}
                  />
                </div>
              </Grid>
            </Grid>
          </DocumentPDF>
        </div>
      </Modal>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Print Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button 
          onClick={() => setShowPrintModal(true)}
          startIcon={<IconGenerator type="pdf" />}
          variant="outlined"
          size="small"
        >
          <Translate id="general.print" />
        </Button>
      </Box>
      <Typography variant="h6">
        {<Translate id="hospital.analytics.finance.patient_category_billing.title" />}
      </Typography>
      {formatDateRange() && (
        <Typography variant="body2" color="text.secondary">
          Report Period: {formatDateRange()}
        </Typography>
      )}
      {/* Main Table */}
      {/*
        SHER-853: `noFooter` makes EnhancedTable set rowsPerPage = rows.length, so every one
        of the window's 1234 lines was in the DOM at once and each re-render walked all of
        them. The screen is paginated; the print modal below keeps `noFooter` on purpose,
        because the PDF has to contain the whole report.
      */}
      <EnhancedTable
        noHeader
        noSelectable
        rows={rows}
        headCells={headCells}
      />

      {/* Print Modal */}
      {renderPrintModal()}
    </Box>
  );
};

export default PatientCategoryBilling; 